import React, { useState, useMemo } from 'react';
import { BiSortAlt2, BiSortUp, BiSortDown } from "react-icons/bi";
import styles from './SortableTable.module.css';

export default function SortableTable({
    headers,
    data,
    renderRow,
    className = '',
    onRowClick = null
}) {
    const [sortConfig, setSortConfig] = useState({
        key: headers[0]?.key || null,
        direction: 'ASC'
    });

    const sortedData = useMemo(() => {
        if (!sortConfig.key || !Array.isArray(data) || data.length === 0) {
            return data;
        }

        try {
            const sortedArray = [...data].sort((a, b) => {
                // Handle nested object properties (e.g., 'tableData.averageChipsStack')
                const getValue = (obj, key) => {
                    return key.split('.').reduce((current, k) => current?.[k], obj);
                };

                const aValue = getValue(a, sortConfig.key);
                const bValue = getValue(b, sortConfig.key);

                // Handle numeric values
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortConfig.direction === 'ASC' ? aValue - bValue : bValue - aValue;
                }

                // Handle string values
                const aStr = String(aValue || '').toLowerCase();
                const bStr = String(bValue || '').toLowerCase();

                if (aStr < bStr) return sortConfig.direction === 'ASC' ? -1 : 1;
                if (aStr > bStr) return sortConfig.direction === 'ASC' ? 1 : -1;
                return 0;
            });
            return sortedArray;
        } catch (err) {
            console.error('SortableTable sorting error:', err);
            return data;
        }
    }, [data, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'ASC' ? 'DESC' : 'ASC'
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <BiSortAlt2 size={16} />;
        }
        return sortConfig.direction === 'ASC'
            ? <BiSortDown size={16} />
            : <BiSortUp size={16} />;
    };

    const isHeaderActive = (key) => {
        return sortConfig.key === key;
    };

    return (
        <div className={`${styles.tableContainer} ${className}`}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
                {headers.map((header) => (
                    <div
                        key={header.key}
                        className={`${styles.headerCell} ${styles[header.align || 'alignLeft']} ${
                            isHeaderActive(header.key) ? styles.headerCellActive : ''
                        }`}
                        onClick={() => handleSort(header.key)}
                    >
                        <span
                            className={`${styles.headerText} ${isHeaderActive(header.key) ? styles.headerTextActive : styles.headerTextInactive}`}
                        >
                            {header.label}
                        </span>
                        <span className={`${styles.sortIcon} ${
                            isHeaderActive(header.key) ? styles.sortIconActive : ''
                        }`}>
                            {getSortIcon(header.key)}
                        </span>
                    </div>
                ))}
            </div>

            {/* Table Body */}
            <div className={styles.tableBody}>
                {sortedData.map((item, index) => (
                    <div
                        key={index}
                        className={styles.tableRow}
                        onClick={() => onRowClick && onRowClick(item, index)}
                    >
                        {renderRow(item, index)}
                    </div>
                ))}
            </div>
        </div>
    );
} 